import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, GraduationCap, Send, Users } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import {
  useGetOnboardingConfirmations,
  useSendOnboardingConfirmation,
} from '../../hooks/backend/interviews'
import { writeErrorMessage } from '../../utils/writeError'

// Training and induction confirmations, TR-01 and IN-01.
//
// WHY THIS PANEL EXISTS. The whole backend for these was built and proven end to
// end on 27 August 2026: two SignNow templates with every field placed from
// signnow_template_field_specs, an edge function, eleven migrations, a live send
// and a signature that came back. NOTHING IN THE COCKPIT COULD REACH ANY OF IT.
// 82 obligations, two per active driver, were outstanding with no button.
//
// THE MANAGER SIGNS FIRST AND THE DRIVER SECOND, and that ordering is the whole
// design rather than a detail. The manager records what actually happened at the
// session; the driver then signs against that record. The reverse would be a
// driver certifying his own training.
//
// THE TICKS ARE THE POINT. Each module the manager ticks here arrives on the
// document ALREADY TICKED, because the fields are placed on the driver's own
// copy at send time rather than living on the template. That is why the module
// list comes from the database and is not written out in this file: it is the
// same list the PDF was drawn from, so the screen and the document cannot drift.

type Module = { name: string; label: string }

type ConfirmationRow = {
  kind: 'training' | 'induction'
  status: string
  manager_done: boolean
  manager_name: string | null
  manager_completed_at: string | null
  sent_at: string | null
  signed_at: string | null
  envelope_id: string | null
  days_outstanding: number | null
  template_ready: boolean
  // Why the last send did not reach SignNow, written by the edge function
  // (migration 0364). Before that, a refused send left no trace at all.
  send_error: string | null
  // Who SignNow accepted as a cc: the driver's own manager (migration 0365).
  copied_to: string | null
  modules: Module[] | null
  recorded: Record<string, unknown> | null
}

type PanelData = { rows: ConfirmationRow[]; canAct: boolean; actorLinked: boolean }

const TITLE: Record<string, string> = {
  training: 'Training confirmation (TR-01)',
  induction: 'Induction confirmation (IN-01)',
}

const WHO_SIGNS: Record<string, string> = {
  training:
    'You sign first as the trainer, then the driver signs to confirm he received it.',
  induction:
    'You sign first as the facilitator, then the driver signs to confirm he attended.',
}

function statusLine(row: ConfirmationRow): string {
  if (row.status === 'signed') {
    return `Signed${row.signed_at ? ` on ${row.signed_at.slice(0, 10)}` : ''}.`
  }
  if (row.status === 'waived') return 'Waived.'
  if (row.status === 'sent') {
    return `Out for signature${row.sent_at ? ` since ${row.sent_at.slice(0, 10)}` : ''}. The driver is notified once you have signed.`
  }
  return row.days_outstanding != null
    ? `Not recorded yet. Outstanding ${row.days_outstanding} day(s).`
    : 'Not recorded yet.'
}

function todayISO(): string {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

function ConfirmationCard({
  row,
  canAct,
  onSend,
  busy,
}: {
  row: ConfirmationRow
  canAct: boolean
  busy: boolean
  onSend: (
    kind: string,
    modules: Record<string, boolean>,
    eventDate: string,
    notes: string,
    mode: string,
  ) => Promise<void>
}) {
  const modules = row.modules ?? []
  const recorded = (row.recorded?.modules ?? {}) as Record<string, unknown>

  const [ticks, setTicks] = useState<Record<string, boolean>>({})
  const [eventDate, setEventDate] = useState(todayISO())
  const [notes, setNotes] = useState('')
  const [open, setOpen] = useState(false)

  // Whatever the manager recorded last time comes back ticked, so a resend does
  // not silently lose what he already said happened.
  useEffect(() => {
    const seed: Record<string, boolean> = {}
    for (const m of modules) seed[m.name] = recorded[m.name] === true
    setTicks(seed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.kind, row.status])

  const isSigned = row.status === 'signed' || row.status === 'waived'
  const isOut = row.status === 'sent'
  const ticked = Object.values(ticks).filter(Boolean).length

  return (
    <div className="rounded-md border border-border bg-background p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          {row.kind === 'training' ? (
            <GraduationCap className="h-4 w-4 text-[hsl(var(--brand))]" />
          ) : (
            <Users className="h-4 w-4 text-[hsl(var(--brand))]" />
          )}
          <span className="text-sm font-medium text-foreground">{TITLE[row.kind]}</span>
        </div>
        {isSigned && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
      </div>

      <p className="mt-1 text-xs text-muted-foreground">{statusLine(row)}</p>
      {row.manager_done && row.manager_name && (
        <p className="text-xs text-muted-foreground">
          Recorded by {row.manager_name}
          {row.manager_completed_at ? ` on ${row.manager_completed_at.slice(0, 10)}` : ''}.
        </p>
      )}

      {row.copied_to && (isOut || isSigned) && (
        <p className="text-xs text-muted-foreground">Copied to the driver&apos;s manager at {row.copied_to}.</p>
      )}

      {row.send_error && !isSigned && (
        <p className="mt-2 flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Not sent. {row.send_error}</span>
        </p>
      )}

      {/* The template gate is answered by the database, not guessed here. A
          missing template id must say so now rather than fail at send time. */}
      {!row.template_ready && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          The SignNow template for this document is not configured, so it cannot be sent yet.
        </p>
      )}

      {canAct && !isSigned && row.template_ready && (
        <>
          {!open ? (
            <Button size="sm" className="mt-3" onClick={() => setOpen(true)}>
              <Send className="mr-2 h-4 w-4" />
              {isOut ? 'Record again and resend' : 'Record and send'}
            </Button>
          ) : (
            <div className="mt-3 space-y-3">
              <p className="text-xs text-muted-foreground">{WHO_SIGNS[row.kind]}</p>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor={`date-${row.kind}`} className="text-xs">
                    Date of the session
                  </Label>
                  <Input
                    id={`date-${row.kind}`}
                    type="date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor={`notes-${row.kind}`} className="text-xs">
                    Notes, optional
                  </Label>
                  <Input
                    id={`notes-${row.kind}`}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Anything worth recording"
                  />
                </div>
              </div>

              {modules.length > 0 ? (
                <div>
                  <p className="mb-1.5 text-xs font-medium text-foreground">
                    Tick only what was actually covered. {ticked} of {modules.length}.
                  </p>
                  <div className="space-y-1.5">
                    {modules.map((m) => (
                      <label key={m.name} className="flex items-start gap-2 text-sm">
                        <Checkbox
                          checked={ticks[m.name] === true}
                          onCheckedChange={(v) =>
                            setTicks((t) => ({ ...t, [m.name]: v === true }))
                          }
                          className="mt-0.5"
                        />
                        <span className="text-foreground">{m.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No module list is configured for this document, so it will go out with the text
                  fields filled and the boxes blank for you to tick on the document itself.
                </p>
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    onSend(row.kind, ticks, eventDate, notes, isOut ? 'resend' : 'send')
                  }
                >
                  {busy ? <Spinner className="mr-2 h-4 w-4" /> : <Send className="mr-2 h-4 w-4" />}
                  Send for signature
                </Button>
                <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export function OnboardingConfirmationsPanel({ applicationId }: { applicationId: string }) {
  const panel = useGetOnboardingConfirmations<PanelData>()
  const send = useSendOnboardingConfirmation()
  const [busy, setBusy] = useState<string | null>(null)

  const trigger = panel.trigger
  const load = useCallback(() => {
    void trigger({ application_id: applicationId }, { skipCache: true })
  }, [trigger, applicationId])

  useEffect(() => {
    load()
  }, [load])

  const data = panel.data
  const rows = data?.rows ?? []

  async function handleSend(
    kind: string,
    modules: Record<string, boolean>,
    eventDate: string,
    notes: string,
    mode: string,
  ) {
    setBusy(kind)
    try {
      await send.trigger({
        application_id: applicationId,
        kind,
        modules,
        event_date: eventDate || null,
        notes: notes.trim() ? notes.trim() : null,
        mode,
      }).result
      toast.success('Sent. You are asked to sign first, then the driver.')
      load()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  if (panel.loading && !data) {
    return (
      <section className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner className="h-4 w-4" /> Loading training and induction…
        </div>
      </section>
    )
  }

  // Not an error. Confirmations are opened by activation (migration 0086), so a
  // driver who is not yet active has none, and saying so is the true answer.
  if (rows.length === 0) {
    return (
      <section className="rounded-lg border border-border bg-card p-4">
        <div className="mb-1 flex items-center gap-2">
          <GraduationCap className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Training and induction</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          No confirmations are open for this driver. They open when the employment contract is
          signed and the driver is activated.
        </p>
      </section>
    )
  }

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <GraduationCap className="h-4 w-4 text-[hsl(var(--brand))]" />
        <h3 className="text-sm font-semibold text-foreground">Training and induction</h3>
      </div>

      {data?.canAct === false && (
        <p className="mb-3 text-xs text-muted-foreground">
          You can see these but not send them. Recording a session needs Edit drivers on your
          Cockpit access, because you are signing a statement about what this driver was taught.
        </p>
      )}
      {data?.canAct && data?.actorLinked === false && (
        <p className="mb-3 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Your login is not linked to a person record, so the manager half of the document has
          nobody to send to.
        </p>
      )}

      <div className="space-y-3">
        {rows.map((row) => (
          <ConfirmationCard
            key={row.kind}
            row={row}
            canAct={data?.canAct === true && data?.actorLinked === true}
            busy={busy === row.kind}
            onSend={handleSend}
          />
        ))}
      </div>
    </section>
  )
}
